<script lang="ts">
  import { createEventDispatcher } from 'svelte';

  export let word: string = '';
  export let guessedLetters: Set<string> = new Set();
  export let revealAll: boolean = false;
  const dispatch = createEventDispatcher();

  $: displayLetters = word.split('').map((letter) => ({
    letter,
    revealed: revealAll || guessedLetters.has(letter),
  }));

  // Dynamically size slots so the word always fits on one line
  $: slotSize = getSlotSize(word.length);
  $: fontSize = Math.max(14, slotSize - 16);

  function getSlotSize(len: number): number {
    // Container width available for slots (accounting for padding + gaps)
    const gapTotal = (len - 1) * 6;
    const availableWidth = 380;
    const slotWidth = Math.min(48, Math.max(22, Math.floor((availableWidth - gapTotal) / len)));
    return slotWidth;
  }
</script>

<div class="word-display" role="status" aria-label="Word to guess">
  {#each displayLetters as { letter, revealed }, i}
    <span
      class="letter-slot"
      class:revealed
      class:hidden={!revealed}
      style="width: {slotSize}px; font-size: {fontSize}px; animation-delay: {i * 0.05}s"
    >
      {#if revealed}
        <span class="letter-char">{letter.toUpperCase()}</span>
      {:else}
        <span class="letter-blank">_</span>
      {/if}
    </span>
  {/each}
</div>

<style>
  .word-display {
    display: flex;
    flex-wrap: nowrap;
    justify-content: center;
    gap: 6px;
    padding: 20px;
    min-height: 80px;
    overflow: hidden;
    max-width: 100%;
  }

  .letter-slot {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 56px;
    background: var(--slot-bg, #f0f0f0);
    border: 3px solid var(--slot-border, #ddd);
    border-radius: 8px;
    font-family: 'SF Mono', 'Fira Code', 'JetBrains Mono', monospace;
    font-weight: 700;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    flex-shrink: 0;
  }

  .letter-slot.revealed {
    border-color: var(--slot-revealed-border, #27ae60);
    background: var(--slot-revealed-bg, #e8f5e9);
    animation: slotReveal 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55) both;
  }

  .letter-slot.hidden {
    border-color: var(--slot-hidden-border, #bdc3c7);
    background: var(--slot-hidden-bg, #f5f5f5);
  }

  .letter-char {
    color: var(--slot-char-color, #2c3e50);
    font-weight: 700;
  }

  .letter-blank {
    color: var(--slot-blank-color, #95a5a6);
    font-size: 24px;
  }

  @keyframes slotReveal {
    0% {
      transform: rotateX(90deg) scale(0.8);
      opacity: 0;
    }
    50% {
      transform: rotateX(0deg) scale(1.1);
    }
    100% {
      transform: rotateX(0deg) scale(1);
      opacity: 1;
    }
  }
</style>
